import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, interval, Subscription } from 'rxjs';
import { tap, switchMap, startWith } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Notification, NotificationResponse, UnreadCountResponse } from '../models/notification';

@Injectable({
    providedIn: 'root'
})
export class NotificationService {
    private apiUrl = `${environment.baseUrl}notifications`;
    
    // BehaviorSubjects para estado reactivo
    private _notifications = new BehaviorSubject<Notification[]>([]);
    private _unreadCount = new BehaviorSubject<number>(0);
    private _loading = new BehaviorSubject<boolean>(false);
    
    // Observables públicos
    public notifications$ = this._notifications.asObservable();
    public unreadCount$ = this._unreadCount.asObservable();
    public loading$ = this._loading.asObservable();

    // Polling interval (30 segundos)
    private pollingInterval = 30000;
    private pollingSubscription?: Subscription;

    constructor(private http: HttpClient) {
        // Iniciar polling automático
        this.startPolling();
    }

    /**
     * Iniciar polling para actualizar contador de notificaciones no leídas
     */
    startPolling(): void {
        this.pollingSubscription = interval(this.pollingInterval).pipe(
            startWith(0),
            switchMap(() => this.getUnreadCount())
        ).subscribe();
    }

    /**
     * Detener polling
     */
    stopPolling(): void {
        if (this.pollingSubscription) {
            this.pollingSubscription.unsubscribe();
        }
    }

    /**
     * Obtener todas las notificaciones con paginación
     */
    getNotifications(page: number = 1, perPage: number = 15, unreadOnly: boolean = false): Observable<NotificationResponse> {
        this._loading.next(true);
        
        let params = new HttpParams()
            .set('page', page.toString())
            .set('per_page', perPage.toString());

        if (unreadOnly) {
            params = params.set('unread_only', 'true');
        }

        return this.http.get<NotificationResponse>(this.apiUrl, { params }).pipe(
            tap(response => {
                this._notifications.next(response.data);
                this._loading.next(false);
            })
        );
    }

    /**
     * Obtener notificaciones recientes (últimas 5)
     */
    getRecentNotifications(): Observable<Notification[]> {
        return this.http.get<Notification[]>(`${this.apiUrl}/recent`).pipe(
            tap(notifications => {
                this._notifications.next(notifications);
            })
        );
    }

    /**
     * Obtener contador de notificaciones no leídas
     */
    getUnreadCount(): Observable<UnreadCountResponse> {
        return this.http.get<UnreadCountResponse>(`${this.apiUrl}/unread-count`).pipe(
            tap(response => {
                this._unreadCount.next(response.count);
            })
        );
    }

    /**
     * Obtener una notificación específica
     */
    getNotification(id: number): Observable<Notification> {
        return this.http.get<Notification>(`${this.apiUrl}/${id}`);
    }

    /**
     * Marcar una notificación como leída
     */
    markAsRead(id: number): Observable<any> {
        return this.http.post(`${this.apiUrl}/${id}/mark-read`, {}).pipe(
            tap(() => {
                // Actualizar la notificación en el array local
                const notifications = this._notifications.value;
                const index = notifications.findIndex(n => n.id === id);
                if (index !== -1) {
                    notifications[index].read_at = new Date().toISOString();
                    this._notifications.next([...notifications]);
                }
                
                // Decrementar el contador
                const currentCount = this._unreadCount.value;
                if (currentCount > 0) {
                    this._unreadCount.next(currentCount - 1);
                }
            })
        );
    }

    /**
     * Marcar todas las notificaciones como leídas
     */
    markAllAsRead(): Observable<any> {
        return this.http.post(`${this.apiUrl}/mark-all-read`, {}).pipe(
            tap(() => {
                // Actualizar todas las notificaciones localmente
                const notifications = this._notifications.value;
                const updatedNotifications = notifications.map(n => ({
                    ...n,
                    read_at: n.read_at || new Date().toISOString()
                }));
                this._notifications.next(updatedNotifications);
                
                // Resetear contador
                this._unreadCount.next(0);
            })
        );
    }

    /**
     * Marcar una notificación como no leída
     */
    markAsUnread(id: number): Observable<any> {
        return this.http.post(`${this.apiUrl}/${id}/mark-unread`, {}).pipe(
            tap(() => {
                // Actualizar la notificación en el array local
                const notifications = this._notifications.value;
                const index = notifications.findIndex(n => n.id === id);
                if (index !== -1) {
                    notifications[index].read_at = null;
                    this._notifications.next([...notifications]);
                }
                
                // Incrementar el contador
                const currentCount = this._unreadCount.value;
                this._unreadCount.next(currentCount + 1);
            })
        );
    }

    /**
     * Eliminar una notificación
     */
    deleteNotification(id: number): Observable<any> {
        return this.http.delete(`${this.apiUrl}/${id}`).pipe(
            tap(() => {
                // Remover la notificación del array local
                const notifications = this._notifications.value;
                const filtered = notifications.filter(n => n.id !== id);
                this._notifications.next(filtered);
                
                // Actualizar contador si era no leída
                const notification = notifications.find(n => n.id === id);
                if (notification && !notification.read_at) {
                    const currentCount = this._unreadCount.value;
                    if (currentCount > 0) {
                        this._unreadCount.next(currentCount - 1);
                    }
                }
            })
        );
    }

    /**
     * Eliminar todas las notificaciones leídas
     */
    deleteAllRead(): Observable<any> {
        return this.http.delete(`${this.apiUrl}/delete-all-read`).pipe(
            tap(() => {
                // Filtrar solo las no leídas
                const notifications = this._notifications.value;
                const unreadNotifications = notifications.filter(n => !n.read_at);
                this._notifications.next(unreadNotifications);
            })
        );
    }

    /**
     * Refrescar datos de notificaciones
     */
    refresh(): void {
        this.getUnreadCount().subscribe();
        this.getRecentNotifications().subscribe();
    }

    /**
     * Enviar notificación del sistema (solo administradores)
     */
    sendSystemNotification(data: {
        title: string;
        message: string;
        icon: string;
        send_to: 'all' | 'company';
    }): Observable<any> {
        return this.http.post(`${this.apiUrl}/send-system-notification`, data);
    }
}
